import React, { createContext, useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import {
  prefsToConsentParams,
  isRegulatedRegion,
  getCachedGeo,
  setCachedGeo,
} from '../helpers/consentMode'

// before: (nothing)
// after:
function isStorageAvailable(type) {
  try {
    const storage = window[type]
    const key = '__storage_test__'
    storage.setItem(key, key)
    storage.removeItem(key)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @typedef {Object} ConsentPrefs
 * @property {boolean} analytics
 * @property {boolean} marketing
 * @property {boolean} advertising
 */

/** @type {ConsentPrefs} */
export const COOKIE_CATEGORIES = [
  {
    key: 'necessary',
    label: 'Necessary',
    description: 'Required for basic site functionality (always enabled).',
    required: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Helps us understand site usage & performance.',
    required: false,
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description: 'Enables personalized marketing & tracking.',
    required: false,
  },
  {
    key: 'advertising',
    label: 'Advertising',
    description: 'Used by third parties for targeted ads.',
    required: false,
  },
  {
    key: 'liveChat',
    label: 'Live Chat',
    description: 'Supports real-time chat support & transcripts.',
    required: false,
  },
]

// shape of prefs
export const defaultPrefs = COOKIE_CATEGORIES.reduce((acc, { key }) => {
  acc[key] = true
  return acc
}, { confirmed: false })

const STORAGE_KEY = 'cookieConsent'

/**
 * @typedef {Object} CookieConsentContextValue
 * @property {ConsentPrefs} prefs
 * @property {(p: ConsentPrefs) => void} setPrefs
 */

/** @type {CookieConsentContextValue} */
const defaultContext = {
  prefs: defaultPrefs,
  setPrefs: () => {},
  regionEligible: false,
}

const CookieConsentContext = createContext(defaultContext)


const GEO_API = 'https://geolocation-db.com/json/d637fa90-2699-11f0-b1e4-0b6fbd2235a3'  // CORS-friendly, no key required


/**
 * Hook to read & write cookie‐consent prefs
 * @returns {CookieConsentContextValue}
 */
export const useCookieConsent = () => useContext(CookieConsentContext)

/**
 * Provides consent prefs via Context; persists to localStorage+cookie.
 * @param {{ children: React.ReactNode }} props
 */
export const CookieConsentProvider = ({ children }) => {
  const [prefs, _setPrefs] = useState(defaultPrefs)
  const [askOpen, setAskOpen] = useState(false)
  const setPrefs = newPrefs => {
    _setPrefs({ ...newPrefs, confirmed: true })
    // Signal Google Consent Mode v2
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', prefsToConsentParams(newPrefs))
    }
    if (
      typeof window !== 'undefined' &&
      window.location &&
      typeof window.location.reload === 'function'
    ) {
      window.location.reload()
    }
  }
  const [regionEligible, setRegionEligible] = useState(false)

  // load saved prefs
  useEffect(() => {
    const lsOK = isStorageAvailable('localStorage')
    const ssOK = isStorageAvailable('sessionStorage')
    let stored = null
    try {
      if (lsOK) {
        stored = window.localStorage.getItem(STORAGE_KEY)
      } else if (navigator.cookieEnabled) {
        const m = document.cookie.match(new RegExp(`${STORAGE_KEY}=([^;]+)`))
        if (m) stored = decodeURIComponent(m[1])
      } else if (ssOK) {
        stored = window.sessionStorage.getItem(STORAGE_KEY)
      }

      if (stored) {
        _setPrefs(JSON.parse(stored))
      }
    } catch (e) {
      console.warn('Failed to load cookie prefs', e)
    }
  }, [])

  // save on change
  useEffect(() => {
    const lsOK = isStorageAvailable('localStorage')
    const ssOK = isStorageAvailable('sessionStorage')
    const raw = JSON.stringify(prefs)
    const encoded = encodeURIComponent(raw)
    const cookieStr = `${STORAGE_KEY}=${encoded};path=/;max-age=${365 * 24 * 3600}`

    try {
      if (lsOK) {
        window.localStorage.setItem(STORAGE_KEY, raw)
      } else if (navigator.cookieEnabled) {
        document.cookie = cookieStr
      } else if (ssOK) {
        window.sessionStorage.setItem(STORAGE_KEY, raw)
      }
    } catch (e) {
      console.warn('Failed to save cookie prefs', e)
    }
  }, [prefs])

  // 3. Detect region on first mount
  useEffect(() => {
    const cached = getCachedGeo()
    if (cached) {
      if (isRegulatedRegion(cached.country_code, cached.state)) {
        setRegionEligible(true)
      }
      return
    }
    fetch(GEO_API)
      .then(res => res.json())
      .then(data => {
        setCachedGeo(data)
        if (isRegulatedRegion(data.country_code, data.state)) {
          setRegionEligible(true)
        }
      })
      .catch(err => {
        setRegionEligible(true)
        console.warn('Geo lookup failed', err)
      })
  }, [])

  return (
    <CookieConsentContext.Provider value={{ prefs, setPrefs, regionEligible, askOpen, setAskOpen }}>
      {children}
    </CookieConsentContext.Provider>
  )
}
CookieConsentProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
